import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-9c2b3d80"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "meeting-container" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "header" }, [
  /*#__PURE__*/_createElementVNode("h2", null, "Group Meeting")
], -1))
const _hoisted_3 = { class: "controls" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h4", null, "Last Audio Listen Response:", -1))
const _hoisted_6 = { class: "status-message" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("strong", null, "Request ID:", -1))
const _hoisted_8 = { class: "success-value" }
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("strong", null, "Success:", -1))
const _hoisted_10 = { class: "bold" }
const _hoisted_11 = {
  key: 0,
  class: "error-message"
}
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("strong", null, "Server Error:", -1))
const _hoisted_13 = { class: "participants" }
const _hoisted_14 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", null, "Participants", -1))
const _hoisted_15 = {
  key: 0,
  class: "current-user-badge"
}
const _hoisted_16 = {
  key: 1,
  class: "owner-badge"
}
const _hoisted_17 = {
  key: 0,
  class: "no-participants"
}
const _hoisted_18 = {
  key: 1,
  class: "modal-overlay"
}
const _hoisted_19 = { class: "modal-content" }
const _hoisted_20 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", null, "Test Audio Listen Request", -1))
const _hoisted_21 = { class: "form-group" }
const _hoisted_22 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { for: "prompt" }, "Prompt:", -1))
const _hoisted_23 = { class: "form-group" }
const _hoisted_24 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { for: "seconds" }, "Duration (seconds):", -1))

import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { RealtimeAudioStreamingService, ParticipantUpdate } from '../RealtimeApiAudioManager';
import { ToGameServer, ToGameClient, AudioListenRequest, AudioListenResponse } from '../../proto-gen-ref/game_pb';
import { GameStreamingService } from '../GameManager';
import { v4 as uuidv4 } from 'uuid';

interface ParsedParticipant {
  id: string;
  name: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GroupMeeting',
  setup(__props) {

const router = useRouter();
const audioService = RealtimeAudioStreamingService.getInstance();
const gameService = GameStreamingService.getInstance();
const participants = ref<string[]>([]);
const ownerUuids = ref<Set<string>>(new Set());
const currentUserUuid = ref('');

// Audio Listen Request Modal
const showListenModal = ref(false);
const listenRequest = ref({
  prompt: localStorage.getItem('audioListenPrompt') || "is 'red' present in the input?",
  seconds: parseInt(localStorage.getItem('audioListenDuration') || '5')
});
const lastListenResponse = ref<{
  requestId: string;
  success: boolean;
  error?: string;
  status?: string; // New field for status messages like "Waiting for response..."
} | null>(null);
const pendingRequestId = ref<string | null>(null);

const isRecording = computed(() => audioService.isRecording());
const isMicMuted = computed(() => audioService.isMicMuted());

const parsedParticipants = computed<ParsedParticipant[]>(() => {
  return participants.value.map(p => {
    const [id, name] = p.split(':', 2);
    return { id, name };
  });
});

const sortedParticipants = computed(() => {
  return [...parsedParticipants.value].sort((a, b) => 
    a.name.localeCompare(b.name)
  );
});

const canTerminate = computed(() => ownerUuids.value.has(currentUserUuid.value));

const toggleMic = () => {
  audioService.toggleAudioMuting();
};

const handleLeave = async () => {
  if (canTerminate.value) {
    if (confirm('Are you sure you want to end the meeting for all participants?')) {
      await audioService.disconnect(); // This will trigger termination due to our changes in the service
      router.push('/');
    }
  } else {
    await audioService.disconnect();
    router.push('/');
  }
};

// Audio Listen Request functions
const openListenModal = () => {
  // Only allow opening the modal if no request is pending
  if (pendingRequestId.value) {
    console.log('Request already pending, cannot open modal');
    return;
  }
  showListenModal.value = true;
};

const closeListenModal = () => {
  showListenModal.value = false;
};

const sendListenRequest = async () => {
  try {
    // Save values to localStorage for persistence
    localStorage.setItem('audioListenPrompt', listenRequest.value.prompt);
    localStorage.setItem('audioListenDuration', listenRequest.value.seconds.toString());
    
    // Generate a unique request ID
    const requestId = uuidv4();
    
    // Store the current request ID for correlation with response
    pendingRequestId.value = requestId;
    
    // Create the request message
    const audioListenReq = new AudioListenRequest()
      .setRequestId(requestId)
      .setPrompt(listenRequest.value.prompt)
      .setSeconds(listenRequest.value.seconds);
    
    const toGameServer = new ToGameServer()
      .setAudioListenReq(audioListenReq);
    
    // Send the request
    console.log('Sending AudioListenRequest:', {
      requestId,
      prompt: listenRequest.value.prompt,
      seconds: listenRequest.value.seconds
    });
    
    await gameService.send(toGameServer);
    
    // Close the modal
    closeListenModal();
    
    // Show a temporary status
    lastListenResponse.value = {
      requestId,
      success: true,
      error: '',  // No error
      status: 'Waiting for response...'  // Status message
    };
  } catch (error) {
    console.error('Error sending AudioListenRequest:', error);
    lastListenResponse.value = {
      requestId: 'Error',
      success: false,
      error: '', // No server error
      status: `Error sending request to server: ${error instanceof Error ? error.message : String(error)}`
    };
    // Clear the pending request ID on error
    pendingRequestId.value = null;
  }
};

// Function to handle AudioListenResponse
const handleAudioListenResponse = (response: AudioListenResponse) => {
  console.log('Received AudioListenResponse:', {
    requestId: response.getRequestId(),
    success: response.getSuccess(),
    error: response.getError()
  });
  
  lastListenResponse.value = {
    requestId: response.getRequestId(),
    success: response.getSuccess(),
    error: response.getError()
  };
};

onMounted(() => {
  console.log('GroupMeeting component mounted, registering callbacks');
  
  const processParticipantUpdate = (update: ParticipantUpdate) => {
    console.log('Processing participant update:', {
      participants: update.participants,
      ownerUuids: Array.from(update.ownerUuids),
      currentUserUuid: update.currentUserUuid
    });
    participants.value = update.participants;
    ownerUuids.value = update.ownerUuids;
    currentUserUuid.value = update.currentUserUuid;
  };

  const bufferedUpdate = audioService.onParticipantUpdate(processParticipantUpdate);
  if (bufferedUpdate) {
    console.log('Processing buffered participant update');
    processParticipantUpdate(bufferedUpdate);
  }
  
  // Register for audio listen responses
  gameService.onStringMessage((typeCase, message) => {
    console.log('GameManager message received:', { typeCase, message });
    
    // Handle audio listen responses
    if (typeCase === ToGameClient.TypeCase.AUDIO_LISTEN_RESP) {
      try {
        const responseData = JSON.parse(message);
        
        // Check if this response matches our pending request
        if (pendingRequestId.value && responseData.requestId === pendingRequestId.value) {
          console.log('Matched response to pending request:', pendingRequestId.value);
          
          // Update the UI with the response
          lastListenResponse.value = {
            requestId: responseData.requestId,
            success: responseData.success,
            error: responseData.error, // Preserve the server's error message
            status: responseData.error ? 'Error received from server' : 'Response received successfully'
          };
          
          // Clear the pending request ID
          pendingRequestId.value = null;
        } else {
          // Handle responses that don't match our pending request
          console.warn('Received response for unknown or different request:', responseData.requestId);
          
          // Display an error in the UI
          lastListenResponse.value = {
            requestId: 'mismatch-' + responseData.requestId,
            success: false,
            error: '', // No server error
            status: `Received response for unknown or different request ID: ${responseData.requestId}`
          };
          
          // Clear any pending request ID to reset UI state
          pendingRequestId.value = null;
        }
      } catch (error) {
        console.error('Error parsing AUDIO_LISTEN_RESP message:', error);
        
        // Handle JSON parsing errors in the UI
        lastListenResponse.value = {
          requestId: 'parse-error',
          success: false,
          error: '', // No server error
          status: `Error parsing response: ${error instanceof Error ? error.message : String(error)}`
        };
        
        // Clear any pending request ID
        pendingRequestId.value = null;
      }
    }
  });
});

onUnmounted(() => {
  audioService.removeParticipantUpdateListeners();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("button", {
        onClick: toggleMic,
        class: _normalizeClass({ active: !isMicMuted.value })
      }, _toDisplayString(isMicMuted.value ? 'Unmute' : 'Mute') + " Microphone ", 3),
      _createElementVNode("button", {
        onClick: openListenModal,
        class: _normalizeClass(["listen-button", { 'pending': pendingRequestId.value !== null }]),
        disabled: pendingRequestId.value !== null
      }, _toDisplayString(pendingRequestId.value ? 'Request Pending...' : 'Test Audio Listen'), 11, _hoisted_4),
      _createElementVNode("button", {
        onClick: handleLeave,
        class: _normalizeClass(['leave-button', { 'terminate-button': canTerminate.value }])
      }, _toDisplayString(canTerminate.value ? 'End Meeting for All' : 'Leave Meeting'), 3)
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["audio-status", { recording: isRecording.value }])
    }, _toDisplayString(isRecording.value ? 'Recording' : 'Not Recording'), 3),
    (lastListenResponse.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["listen-response", { 
        'error': lastListenResponse.value.error, 
        'completed': !lastListenResponse.value.error && !pendingRequestId.value && !lastListenResponse.value.status.includes('Error'),
        'pending': pendingRequestId.value,
        'client-error': !lastListenResponse.value.error && lastListenResponse.value.status.includes('Error')
      }])
        }, [
          _hoisted_5,
          _createElementVNode("p", _hoisted_6, [
            _createElementVNode("em", null, "Status: " + _toDisplayString(lastListenResponse.value.status), 1)
          ]),
          _createElementVNode("p", null, [
            _hoisted_7,
            _createTextVNode(" " + _toDisplayString(lastListenResponse.value.requestId), 1)
          ]),
          _createElementVNode("p", _hoisted_8, [
            _hoisted_9,
            _createTextVNode(),
            _createElementVNode("span", _hoisted_10, _toDisplayString(lastListenResponse.value.success), 1)
          ]),
          (lastListenResponse.value.error)
            ? (_openBlock(), _createElementBlock("p", _hoisted_11, [
                _hoisted_12,
                _createTextVNode(" " + _toDisplayString(lastListenResponse.value.error), 1)
              ]))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_13, [
      _hoisted_14,
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedParticipants.value, (participant) => {
        return (_openBlock(), _createElementBlock("div", {
          key: participant.id,
          class: "participant"
        }, [
          _createTextVNode(_toDisplayString(participant.name) + " ", 1),
          (participant.id === currentUserUuid.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_15, " You "))
            : _createCommentVNode("", true),
          (ownerUuids.value.has(participant.id))
            ? (_openBlock(), _createElementBlock("span", _hoisted_16, " Owner "))
            : _createCommentVNode("", true)
        ]))
      }), 128)),
      (participants.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_17, " No participants "))
        : _createCommentVNode("", true)
    ]),
    (showListenModal.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
          _createElementVNode("div", _hoisted_19, [
            _hoisted_20,
            _createElementVNode("div", _hoisted_21, [
              _hoisted_22,
              _withDirectives(_createElementVNode("textarea", {
                id: "prompt",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((listenRequest.value.prompt) = $event)),
                placeholder: "Enter your prompt for the audio listen request",
                rows: "4"
              }, null, 512), [
                [_vModelText, listenRequest.value.prompt]
              ])
            ]),
            _createElementVNode("div", _hoisted_23, [
              _hoisted_24,
              _withDirectives(_createElementVNode("input", {
                id: "seconds",
                type: "number",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((listenRequest.value.seconds) = $event)),
                min: "1",
                max: "60"
              }, null, 512), [
                [
                  _vModelText,
                  listenRequest.value.seconds,
                  void 0,
                  { number: true }
                ]
              ])
            ]),
            _createElementVNode("div", { class: "modal-actions" }, [
              _createElementVNode("button", {
                onClick: sendListenRequest,
                class: "send-button"
              }, "Send Request"),
              _createElementVNode("button", {
                onClick: closeListenModal,
                class: "cancel-button"
              }, "Cancel")
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})